import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import type { PersonPreview, SheetAccess, WorkerView } from "./types";

const CAPABILITIES: { key: keyof SheetAccess; label: string }[] = [
  { key: "sees", label: "Can open" },
  { key: "edits", label: "Can edit" },
  { key: "approves", label: "Can approve" },
];

const tabs = (names: string[] | undefined) =>
  names && names.length > 0 ? names.join(", ") : "Nothing";

export const describeWorkers = (view: WorkerView | null) => {
  if (!view) return null;
  if (view.full) {
    return `Every technician (${view.workers})`;
  }
  if (view.teams.length === 0) return "No technicians: not on a team";
  return `${view.workers} ${view.workers === 1 ? "technician" : "technicians"} on ${view.teams.join(", ")}`;
};

interface AccessComparisonProps {
  person: PersonPreview;
}

// Don't recompute who sees what here.
export const AccessComparison = ({ person }: AccessComparisonProps) => {
  if (!person.after) {
    return (
      <Empty className={emptyStateVariants({ layout: "inline" })}>
        <EmptyHeader>
          <EmptyDescription>
            There is no workbook yet to test against. Run payroll once to see
            tabs.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }
  const workersAfter = describeWorkers(person.after_workers);
  const workersBefore = describeWorkers(person.before_workers);

  const rows = [
    ...CAPABILITIES.map(({ key, label }) => ({
      label,
      after: tabs(person.after?.[key]),
      before: tabs(person.before?.[key]),
    })),
    ...(workersAfter
      ? [
          {
            label: "Technicians",
            after: workersAfter,
            before: workersBefore ?? "",
          },
        ]
      : []),
  ];

  return (
    <dl className="grid w-full grid-cols-[7rem_1fr] gap-x-3 gap-y-2">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="pt-px text-xs font-semibold text-subtle">
            {row.label}
          </dt>
          <dd className="m-0">
            <span
              className={cn("block text-sm", {
                "text-ink": row.after !== "Nothing",
                "text-subtle": row.after === "Nothing",
              })}
            >
              {row.after}
            </span>
            {row.before !== row.after && (
              <span className="block text-xs text-subtle">
                Was: {row.before}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};
